import { NETWORK_ERROR } from './errorCodes';
import { apiFetch, API_BASE_URL, ensureCsrfToken } from './client';
import { ApiError, type ApiErrorBody } from './client';
import type { Visibility } from './visibility';

export type VersionStatus = 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED';

export interface ColumnSchema {
  name: string;
  source_name: string;
  type: 'integer' | 'float' | 'boolean' | 'datetime' | 'date' | 'text';
  nullable: boolean;
}

export type ProfileStatus = 'PENDING' | 'READY' | 'FAILED';

export type VersionOrigin = 'UPLOAD' | 'TRANSFORM';

export interface DatasetVersion {
  id: string;
  version: number;
  status: VersionStatus;
  profile_status: ProfileStatus;
  origin: VersionOrigin;
  parent_version: string | null;
  transform_steps: TransformStep[];
  transform_warnings: string[];
  source_filename: string;
  source_format: 'CSV' | 'XLSX' | 'JSON' | '';
  source_bytes: number;
  row_count: number;
  column_count: number;
  column_schema: ColumnSchema[];
  error_code: string;
  error_message: string;
  created_at: string;
  processed_at: string | null;
}

export interface Dataset {
  id: string;
  name: string;
  description: string;
  /** Who may see it, and whether the caller is its owner. */
  visibility: Visibility;
  is_mine: boolean;
  created_at: string;
  updated_at: string;
  version_count: number;
  latest_version: DatasetVersion | null;
}

export interface Paginated<T> {
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface UploadSession {
  id: string;
  name: string;
  filename: string;
  status: string;
  expires_at: string;
  max_upload_bytes: number;
  upload_url: string;
}

export interface PreviewRow {
  [column: string]: string | number | boolean | null;
}

export interface Preview {
  columns: ColumnSchema[];
  rows: PreviewRow[];
  row_count: number;
  returned: number;
}

export const listDatasets = (page = 1): Promise<Paginated<Dataset>> =>
  apiFetch<Paginated<Dataset>>(`/datasets?page=${page}`);

export const getDataset = (id: string): Promise<Dataset> => apiFetch<Dataset>(`/datasets/${id}`);

export const listVersions = (datasetId: string): Promise<DatasetVersion[]> =>
  apiFetch<DatasetVersion[]>(`/datasets/${datasetId}/versions`);

export interface DistributionBin {
  label: string;
  count: number;
  start?: number | null;
  end?: number | null;
}

export interface TopValue {
  value: string | number | boolean | null;
  count: number;
  percentage: number;
}

export interface ColumnProfile {
  name: string;
  source_name: string;
  type: ColumnSchema['type'];
  count: number;
  null_count: number;
  null_percentage: number;
  distinct_count: number;
  distinct_percentage: number;
  is_unique: boolean;
  is_constant: boolean;
  is_empty: boolean;
  top_values: TopValue[];
  distribution: DistributionBin[];
  minimum: number | null;
  maximum: number | null;
  mean: number | null;
  median: number | null;
  std_dev: number | null;
  p25: number | null;
  p75: number | null;
  zero_count: number;
  negative_count: number;
  earliest: string | null;
  latest: string | null;
  span_days: number | null;
  min_length: number | null;
  max_length: number | null;
  mean_length: number | null;
  empty_string_count: number;
}

export interface RelationshipCandidate {
  column: string;
  related_dataset_id: string;
  related_dataset_name: string;
  related_version_id: string;
  related_column: string;
  overlap_percentage: number;
  kind: string;
  confidence: 'strong' | 'possible' | 'weak';
}

/**
 * A numeric pair that moves together. A measurement, never a cause — whatever
 * renders it must say so.
 */
export interface Correlation {
  columns: [string, string];
  coefficient: number;
  direction: 'together' | 'opposite';
  rows_compared: number;
  strength: string;
}

/**
 * Something worth looking at, proposed when profiling finished.
 *
 * A suggestion is a starting point, never a finding: `question` is always
 * phrased as a question, and `basis` says which measured facts prompted it.
 * `definition` (when present) is a ready-to-run analysis.
 */
export interface Suggestion {
  key: string;
  kind: 'analysis' | 'quality' | 'join';
  title: string;
  question: string;
  basis: string;
  columns: string[];
  definition: Record<string, unknown> | null;
  caveat: string;
}

export interface DatasetProfile {
  status: ProfileStatus;
  row_count: number;
  column_count: number;
  duplicate_row_count: number;
  duplicate_row_percentage: number;
  missing_cell_count: number;
  missing_cell_percentage: number;
  completeness_percentage: number;
  empty_column_count: number;
  constant_column_count: number;
  columns: ColumnProfile[];
  relationships: RelationshipCandidate[];
  correlations: Correlation[];
  suggestions: Suggestion[];
  error_message: string;
  computed_at: string | null;
}

/**
 * One step of a preparation recipe.
 *
 * Steps are declarative: a step names an operation and its arguments. Nothing
 * here is code, and the backend validates every field against the dataset's
 * real schema before anything runs.
 */
export type TransformOperation =
  | 'rename'
  | 'change_type'
  | 'drop_columns'
  | 'select_columns'
  | 'filter'
  | 'drop_nulls'
  | 'fill_nulls'
  | 'replace_values'
  | 'drop_duplicates'
  | 'calculated_column'
  | 'group_by'
  | 'sort'
  | 'limit'
  | 'join';

export interface TransformStep {
  op: TransformOperation;
  [argument: string]: unknown;
}

export interface JoinAnalysis {
  how: 'inner' | 'left';
  left_rows: number;
  right_rows: number;
  result_rows: number;
  match_percentage: number;
  unmatched_left_rows: number;
  right_key_is_unique: boolean;
  row_multiplier: number;
  warnings: string[];
  with_dataset_name: string;
}

export interface TransformPreview {
  columns: ColumnSchema[];
  rows: PreviewRow[];
  returned: number;
  row_count: number;
  /** True when the row count comes from a sample rather than the whole file. */
  is_estimate: boolean;
  sampled_from: number;
  warnings: string[];
  /** What each join in the recipe would actually do. */
  joins: JoinAnalysis[];
}

export const previewTransform = (
  datasetId: string,
  versionId: string,
  steps: TransformStep[],
): Promise<TransformPreview> =>
  apiFetch<TransformPreview>(`/datasets/${datasetId}/versions/${versionId}/transform/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ steps }),
  });

export const applyTransform = (
  datasetId: string,
  versionId: string,
  steps: TransformStep[],
): Promise<DatasetVersion> =>
  apiFetch<DatasetVersion>(`/datasets/${datasetId}/versions/${versionId}/transform`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ steps }),
  });

export const getVersion = (datasetId: string, versionId: string): Promise<DatasetVersion> =>
  apiFetch<DatasetVersion>(`/datasets/${datasetId}/versions/${versionId}`);

export const getProfile = (datasetId: string, versionId: string): Promise<DatasetProfile> =>
  apiFetch<DatasetProfile>(`/datasets/${datasetId}/versions/${versionId}/profile`);

export const recomputeProfile = (datasetId: string, versionId: string): Promise<void> =>
  apiFetch<void>(`/datasets/${datasetId}/versions/${versionId}/profile`, { method: 'POST' });

/**
 * Process a version that did not finish (Phase 32).
 *
 * The file is already stored and the work is idempotent, so this is a second
 * attempt at the same bytes rather than a second upload of them.
 */
export const retryVersion = (datasetId: string, versionId: string): Promise<DatasetVersion> =>
  apiFetch<DatasetVersion>(`/datasets/${datasetId}/versions/${versionId}/retry`, {
    method: 'POST',
  });

export const getPreview = (datasetId: string, versionId: string, limit = 20): Promise<Preview> =>
  apiFetch<Preview>(`/datasets/${datasetId}/versions/${versionId}/preview?limit=${limit}`);

export const renameDataset = (id: string, name: string, description?: string): Promise<Dataset> =>
  apiFetch<Dataset>(`/datasets/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, ...(description === undefined ? {} : { description }) }),
  });

export const deleteDataset = (id: string): Promise<void> =>
  apiFetch<void>(`/datasets/${id}`, { method: 'DELETE' });

export const openUploadSession = (input: {
  filename: string;
  name?: string;
  description?: string;
  dataset_id?: string;
}): Promise<UploadSession> =>
  apiFetch<UploadSession>('/datasets/uploads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

/**
 * Send the file itself.
 *
 * This one call bypasses `apiFetch`: the body is `FormData`, which must set its
 * own multipart boundary, and progress needs XHR. The CSRF and credential rules
 * are the same.
 */
export async function uploadFile(
  session: UploadSession,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<DatasetVersion> {
  const token = await ensureCsrfToken();

  const body = new FormData();
  body.append('file', file);

  return new Promise<DatasetVersion>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', `${API_BASE_URL}/datasets/uploads/${session.id}/file`, true);
    request.withCredentials = true;

    if (token) {
      request.setRequestHeader('X-CSRFToken', token);
    }

    request.upload.addEventListener('progress', (event) => {
      if (onProgress && event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });

    request.addEventListener('load', () => {
      let payload: unknown = null;
      try {
        payload = JSON.parse(request.responseText) as unknown;
      } catch {
        payload = null;
      }

      if (request.status >= 200 && request.status < 300) {
        resolve(payload as DatasetVersion);
        return;
      }

      const body = payload as ApiErrorBody | null;
      reject(
        new ApiError(
          request.status,
          body?.error ?? {
            code: 'UPLOAD_FAILED',
            message: 'The upload did not complete. Please try again.',
          },
        ),
      );
    });

    request.addEventListener('error', () =>
      reject(
        new ApiError(0, {
          code: NETWORK_ERROR,
          message: 'The upload could not reach Simple Coffee. Check your connection.',
        }),
      ),
    );

    request.send(body);
  });
}

/** Human-readable file size, e.g. "1.4 MB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}
